import React, { useState, useEffect } from "react";
import {
  TrendingUp,
  TrendingDown,
  Calendar,
  Download,
  RefreshCw,
  Database,
  FileText,
  AlertTriangle,
  Shield,
  Users,
  CheckCircle,
  Loader2,
  BarChart3,
  PieChart,
  Activity,
} from "lucide-react";
import { supabase } from "../lib/supabase";

// ============================================
// FONCTIONS SUPABASE - CHARGEMENT DES STATS
// ============================================

const loadAllStats = async (entrepriseId) => {
  const stats = {
    inventaire: { total: 0, sensibles: 0 },
    documents: { total: 0 },
    incidents: { total: 0, enCours: 0, resolus: 0, parMois: [] },
    efvp: { total: 0, enCours: 0, termines: 0 },
    consentements: { total: 0, actifs: 0 },
    utilisateurs: { total: 0 },
  };

  try {
    // Inventaire des données
    const { data: inventaire } = await supabase
      .from("inventaire_donnees")
      .select("id, sensibilite")
      .eq("entreprise_id", entrepriseId);

    if (inventaire) {
      stats.inventaire.total = inventaire.length;
      // Valeurs considérées comme sensibles
      const sensibleValues = [
        "élevée",
        "elevee",
        "haute",
        "high",
        "critique",
        "critical",
        "très confidentiel",
        "tres confidentiel",
        "très-confidentiel",
        "confidentiel",
        "confidential",
        "sensible",
        "sensitive",
      ];
      stats.inventaire.sensibles = inventaire.filter((i) => {
        const val = (i.sensibilite || "").toLowerCase().trim();
        return sensibleValues.some((s) => val.includes(s) || s.includes(val));
      }).length;
    }

    // Documents
    const { data: documents } = await supabase
      .from("documents")
      .select("id")
      .eq("entreprise_id", entrepriseId);

    if (documents) {
      stats.documents.total = documents.length;
    }

    // Incidents
    const { data: incidents } = await supabase
      .from("incidents")
      .select("id, statut, created_at, severite")
      .eq("entreprise_id", entrepriseId);

    if (incidents) {
      stats.incidents.total = incidents.length;
      stats.incidents.enCours = incidents.filter(
        (i) => i.statut === "En cours" || i.statut === "En analyse"
      ).length;
      stats.incidents.resolus = incidents.filter(
        (i) => i.statut === "Résolu" || i.statut === "Fermé"
      ).length;

      // Grouper par mois (6 derniers mois)
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

      const monthlyIncidents = {};
      incidents.forEach((inc) => {
        const date = new Date(inc.created_at);
        if (date >= sixMonthsAgo) {
          const monthKey = `${date.getFullYear()}-${String(
            date.getMonth() + 1
          ).padStart(2, "0")}`;
          monthlyIncidents[monthKey] = (monthlyIncidents[monthKey] || 0) + 1;
        }
      });

      // Convertir en tableau pour le graphique
      const months = [
        "Jan",
        "Fév",
        "Mar",
        "Avr",
        "Mai",
        "Juin",
        "Juil",
        "Août",
        "Sep",
        "Oct",
        "Nov",
        "Déc",
      ];
      const now = new Date();
      for (let i = 5; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
          2,
          "0"
        )}`;
        stats.incidents.parMois.push({
          month: months[d.getMonth()],
          count: monthlyIncidents[key] || 0,
        });
      }
    }

    // EFVP
    const { data: efvp } = await supabase
      .from("efvp")
      .select(
        "id, statut, progression, niveau_risque_global, communication_hors_quebec"
      )
      .eq("entreprise_id", entrepriseId);

    if (efvp) {
      stats.efvp.total = efvp.length;

      // Classification EXCLUSIVE
      efvp.forEach((e) => {
        if (
          e.statut === "Terminé" ||
          e.statut === "Approuvé" ||
          e.progression === 100
        ) {
          stats.efvp.termines++;
        } else if (e.statut === "En cours" || e.statut === "En révision") {
          stats.efvp.enCours++;
        }
      });
    }

    // Consentements
    const { data: consentements } = await supabase
      .from("consentements")
      .select("id, actif")
      .eq("entreprise_id", entrepriseId);

    if (consentements) {
      stats.consentements.total = consentements.length;
      stats.consentements.actifs = consentements.filter((c) => c.actif).length;
    }

    // Utilisateurs (profils)
    const { data: profils } = await supabase
      .from("profils")
      .select("id")
      .eq("organisation_id", entrepriseId);

    if (profils) {
      stats.utilisateurs.total = profils.length;
    }
  } catch (error) {
    console.error("Erreur chargement stats:", error);
  }

  return stats;
};

// ============================================
// COMPOSANTS UI
// ============================================

const LoadingSpinner = ({ message = "Chargement..." }) => (
  <div className="flex flex-col items-center justify-center py-12">
    <Loader2 size={40} className="text-sky-600 animate-spin mb-4" />
    <p className="text-gray-500 dark:text-gray-400">{message}</p>
  </div>
);

const StatCard = ({
  title,
  value,
  subtitle,
  icon: Icon,
  gradient,
  trend,
  trendValue,
}) => (
  <div className={`${gradient} text-white p-6 rounded-xl shadow-lg`}>
    <div className="flex items-center justify-between mb-2">
      <p className="text-white/80 text-sm">{title}</p>
      <Icon size={20} />
    </div>
    <p className="text-4xl font-bold mb-1">{value}</p>
    <div className="flex items-center text-sm text-white/80">
      {trend === "up" && <TrendingUp size={14} className="mr-1" />}
      {trend === "down" && <TrendingDown size={14} className="mr-1" />}
      <span>{subtitle}</span>
    </div>
  </div>
);

const EmptyState = ({ onRefresh }) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-12 text-center">
    <BarChart3
      size={64}
      className="mx-auto text-gray-300 dark:text-gray-600 mb-4"
    />
    <h3 className="text-xl font-bold text-gray-700 dark:text-gray-300 mb-2">
      Pas encore de données
    </h3>
    <p className="text-gray-500 dark:text-gray-400 mb-6">
      Commencez à utiliser les modules (Inventaire, Documents, Incidents...)
      pour voir vos statistiques ici.
    </p>
    <button
      onClick={onRefresh}
      className="bg-sky-600 text-white px-6 py-2 rounded-lg hover:bg-sky-700 inline-flex items-center gap-2"
    >
      <RefreshCw size={18} />
      Actualiser
    </button>
  </div>
);

// ============================================
// COMPOSANT PRINCIPAL
// ============================================

const EnhancedDashboard = ({ entrepriseId, showToast }) => {
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [dateRange, setDateRange] = useState("30days");

  useEffect(() => {
    if (entrepriseId) {
      loadData();
    } else {
      setIsLoading(false);
    }
  }, [entrepriseId]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const data = await loadAllStats(entrepriseId);
      setStats(data);
    } catch (err) {
      console.error("Erreur:", err);
      if (showToast)
        showToast("Erreur lors du chargement des statistiques.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await loadData();
    setIsRefreshing(false);
    if (showToast) showToast("Données actualisées!", "success");
  };

  const handleExport = () => {
    if (!stats) return;

    const reportData = {
      date: new Date().toISOString(),
      entrepriseId,
      statistiques: stats,
    };

    const blob = new Blob([JSON.stringify(reportData, null, 2)], {
      type: "application/json",
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `rapport_conformite_${
      new Date().toISOString().split("T")[0]
    }.json`;
    a.click();

    if (showToast) showToast("Rapport exporté!", "success");
  };

  // Calcul du score de conformité (simplifié)
  const calculateScore = () => {
    if (!stats) return 0;

    let score = 0;
    let maxScore = 0;

    // Points pour l'inventaire
    if (stats.inventaire.total > 0) score += 20;
    maxScore += 20;

    // Points pour les documents
    if (stats.documents.total > 0) score += 20;
    maxScore += 20;

    // Points pour les EFVP
    if (stats.efvp.total > 0) {
      score += 15;
      if (stats.efvp.termines > 0) score += 5;
    }
    maxScore += 20;

    // Points pour la gestion des incidents
    if (stats.incidents.total > 0 && stats.incidents.resolus > 0) score += 15;
    maxScore += 15;

    // Points pour les consentements
    if (stats.consentements.total > 0) score += 15;
    maxScore += 15;

    // Points pour les utilisateurs multiples
    if (stats.utilisateurs.total > 1) score += 10;
    maxScore += 10;

    return maxScore > 0 ? Math.round((score / maxScore) * 100) : 0;
  };

  // États de chargement
  if (!entrepriseId) {
    return (
      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-6 text-center">
        <AlertTriangle size={40} className="text-amber-500 mx-auto mb-4" />
        <p className="text-amber-700 dark:text-amber-300">
          Veuillez sélectionner une entreprise.
        </p>
      </div>
    );
  }

  if (isLoading)
    return <LoadingSpinner message="Chargement des statistiques..." />;

  const score = calculateScore();
  const hasData =
    stats &&
    (stats.inventaire.total > 0 ||
      stats.documents.total > 0 ||
      stats.incidents.total > 0 ||
      stats.efvp.total > 0 ||
      stats.consentements.total > 0);

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 dark:text-white">
            Tableau de bord analytique
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Vue d'ensemble de votre conformité Loi 25
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-sky-500"
          >
            <option value="7days">7 derniers jours</option>
            <option value="30days">30 derniers jours</option>
            <option value="3months">3 derniers mois</option>
            <option value="6months">6 derniers mois</option>
          </select>

          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <RefreshCw
              size={20}
              className={`text-gray-600 dark:text-gray-400 ${
                isRefreshing ? "animate-spin" : ""
              }`}
            />
          </button>

          <button
            onClick={handleExport}
            disabled={!hasData}
            className="flex items-center space-x-2 px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download size={20} />
            <span>Exporter</span>
          </button>
        </div>
      </div>

      {!hasData ? (
        <EmptyState onRefresh={handleRefresh} />
      ) : (
        <>
          {/* KPIs principaux */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              title="Score de conformité"
              value={`${score}%`}
              subtitle={
                score >= 70
                  ? "Bon niveau"
                  : score >= 40
                  ? "En progression"
                  : "À améliorer"
              }
              icon={Activity}
              gradient="bg-gradient-to-br from-sky-500 to-sky-600"
              trend={score >= 50 ? "up" : "down"}
            />

            <StatCard
              title="Données inventoriées"
              value={stats.inventaire.total}
              subtitle={`${stats.inventaire.sensibles} sensible${
                stats.inventaire.sensibles > 1 ? "s" : ""
              }`}
              icon={Database}
              gradient="bg-gradient-to-br from-emerald-500 to-emerald-600"
            />

            <StatCard
              title="Incidents"
              value={stats.incidents.total}
              subtitle={`${stats.incidents.enCours} en cours`}
              icon={AlertTriangle}
              gradient="bg-gradient-to-br from-orange-500 to-orange-600"
            />

            <StatCard
              title="Consentements"
              value={stats.consentements.total}
              subtitle={`${stats.consentements.actifs} actif${
                stats.consentements.actifs > 1 ? "s" : ""
              }`}
              icon={CheckCircle}
              gradient="bg-gradient-to-br from-purple-500 to-purple-600"
            />
          </div>

          {/* Graphiques */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Incidents par mois */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                  Incidents par mois
                </h3>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  6 derniers mois
                </span>
              </div>

              {stats.incidents.parMois.length > 0 &&
              stats.incidents.parMois.some((m) => m.count > 0) ? (
                <div className="relative h-64">
                  <svg className="w-full h-full" viewBox="0 0 400 200">
                    {/* Grille */}
                    {[0, 2, 4, 6, 8].map((y, i) => (
                      <g key={i}>
                        <line
                          x1="40"
                          y1={180 - y * 20}
                          x2="380"
                          y2={180 - y * 20}
                          stroke="currentColor"
                          strokeWidth="1"
                          className="text-gray-200 dark:text-gray-700"
                        />
                        <text
                          x="10"
                          y={185 - y * 20}
                          fontSize="12"
                          className="fill-gray-400"
                        >
                          {y}
                        </text>
                      </g>
                    ))}

                    {/* Barres */}
                    {stats.incidents.parMois.map((d, i) => (
                      <g key={i}>
                        <rect
                          x={60 + i * 55}
                          y={180 - Math.min(d.count * 20, 160)}
                          width="40"
                          height={Math.min(d.count * 20, 160)}
                          fill={d.count > 3 ? "#f97316" : "#10b981"}
                          rx="4"
                        />
                        {d.count > 0 && (
                          <text
                            x={80 + i * 55}
                            y={170 - Math.min(d.count * 20, 160)}
                            fontSize="12"
                            className="fill-gray-700 dark:fill-gray-300"
                            textAnchor="middle"
                            fontWeight="bold"
                          >
                            {d.count}
                          </text>
                        )}
                        <text
                          x={80 + i * 55}
                          y="195"
                          fontSize="11"
                          className="fill-gray-500"
                          textAnchor="middle"
                        >
                          {d.month}
                        </text>
                      </g>
                    ))}
                  </svg>
                </div>
              ) : (
                <div className="h-64 flex items-center justify-center">
                  <div className="text-center">
                    <CheckCircle
                      size={48}
                      className="mx-auto text-green-400 mb-2"
                    />
                    <p className="text-gray-500 dark:text-gray-400">
                      Aucun incident récent
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Répartition des modules */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                  Utilisation des modules
                </h3>
                <PieChart size={20} className="text-gray-400" />
              </div>

              <div className="space-y-4">
                {[
                  {
                    label: "Inventaire",
                    value: stats.inventaire.total,
                    color: "bg-sky-500",
                    max: 20,
                  },
                  {
                    label: "Documents",
                    value: stats.documents.total,
                    color: "bg-emerald-500",
                    max: 20,
                  },
                  {
                    label: "EFVP",
                    value: stats.efvp.total,
                    color: "bg-purple-500",
                    max: 10,
                  },
                  {
                    label: "Consentements",
                    value: stats.consentements.total,
                    color: "bg-amber-500",
                    max: 50,
                  },
                  {
                    label: "Incidents",
                    value: stats.incidents.total,
                    color: "bg-red-500",
                    max: 10,
                  },
                ].map((item, i) => (
                  <div key={i}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {item.label}
                      </span>
                      <span className="text-sm font-bold text-gray-900 dark:text-white">
                        {item.value}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div
                        className={`${item.color} h-2 rounded-full transition-all duration-500`}
                        style={{
                          width: `${Math.min(
                            (item.value / item.max) * 100,
                            100
                          )}%`,
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Résumé des EFVP */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
              Évaluations des facteurs relatifs à la vie privée (EFVP)
            </h3>

            {stats.efvp.total > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-blue-700 dark:text-blue-400">
                    {stats.efvp.total}
                  </p>
                  <p className="text-sm text-blue-600 dark:text-blue-500">
                    Total EFVP
                  </p>
                </div>
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-amber-700 dark:text-amber-400">
                    {stats.efvp.enCours}
                  </p>
                  <p className="text-sm text-amber-600 dark:text-amber-500">
                    En cours
                  </p>
                </div>
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-green-700 dark:text-green-400">
                    {stats.efvp.termines}
                  </p>
                  <p className="text-sm text-green-600 dark:text-green-500">
                    Terminés
                  </p>
                </div>
              </div>
            ) : (
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 text-center">
                <Shield size={40} className="mx-auto text-gray-400 mb-2" />
                <p className="text-gray-600 dark:text-gray-400">
                  Aucune EFVP créée. Créez votre première évaluation dans le
                  module EFVP.
                </p>
              </div>
            )}
          </div>

          {/* Conseils */}
          <div className="bg-gradient-to-r from-sky-50 to-indigo-50 dark:from-sky-900/20 dark:to-indigo-900/20 border border-sky-200 dark:border-sky-800 rounded-xl p-6">
            <h3 className="text-lg font-bold text-sky-900 dark:text-sky-300 mb-4 flex items-center">
              <CheckCircle className="mr-2" size={20} />
              Prochaines étapes recommandées
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {stats.inventaire.total === 0 && (
                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border-l-4 border-sky-500">
                  <p className="font-medium text-gray-900 dark:text-white">
                    Créer votre inventaire de données
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Identifiez les renseignements personnels que vous détenez.
                  </p>
                </div>
              )}
              {stats.documents.total === 0 && (
                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border-l-4 border-emerald-500">
                  <p className="font-medium text-gray-900 dark:text-white">
                    Ajouter vos politiques
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Téléversez votre politique de confidentialité.
                  </p>
                </div>
              )}
              {stats.efvp.total === 0 && (
                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border-l-4 border-purple-500">
                  <p className="font-medium text-gray-900 dark:text-white">
                    Réaliser une EFVP
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Évaluez les risques de vos projets impliquant des données.
                  </p>
                </div>
              )}
              {score >= 70 && (
                <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border-l-4 border-green-500">
                  <p className="font-medium text-gray-900 dark:text-white">
                    Excellent travail! 🎉
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Continuez à maintenir vos données à jour.
                  </p>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default EnhancedDashboard;
